import React, { useState, useEffect } from "react";
import {
  Box,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Button,
  Typography,
  CircularProgress,
  Card,
  CardContent,
  Paper,
  AppBar,
  Tooltip,
  IconButton,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  Snackbar,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import {
  FirstPage,
  NavigateBefore,
  NavigateNext,
  LastPage,
  Visibility,
  Assignment,
  History,
  Receipt,
} from "@mui/icons-material";
import { GET, POST, PUT } from "APIclient";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import { useNavigate } from "react-router-dom";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import MDInput from "components/MDInput";
import properties from "constants/ApplicationConstants";

function CustomPagination({
  page,
  pageSize,
  totalRows,
  totalPages,
  onPageChange,
  onGoToLastPage,
  loading,
}) {
  const handleFirstPage = () => onPageChange(0);
  const handlePreviousPage = () => onPageChange(Math.max(0, page - 1));
  const handleNextPage = () => onPageChange(Math.min(totalPages - 1, page + 1));
  const handleLastPage = () => onGoToLastPage();

  const startRow = page * pageSize + 1;
  const endRow = Math.min((page + 1) * pageSize, totalRows);

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        p: 1,
        borderTop: "1px solid #e0e0e0",
        backgroundColor: "#fafafa",
      }}
    >
      <Typography variant="caption" sx={{ color: "#666", fontFamily: properties.fontFamily }}>
        {totalRows > 0 ? `${startRow}-${endRow} of ${totalRows}` : "No rows"}
      </Typography>

      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        <Typography variant="caption" sx={{ mr: 2, color: "#666", fontFamily: properties.fontFamily }}>
          Page {page + 1} of {totalPages || 1}
        </Typography>

        <Tooltip title="First Page">
          <IconButton
            onClick={handleFirstPage}
            disabled={page === 0 || loading}
            size="small"
          >
            <FirstPage />
          </IconButton>
        </Tooltip>

        <Tooltip title="Previous Page">
          <IconButton
            onClick={handlePreviousPage}
            disabled={page === 0 || loading}
            size="small"
          >
            <NavigateBefore />
          </IconButton>
        </Tooltip>

        <Tooltip title="Next Page">
          <IconButton
            onClick={handleNextPage}
            disabled={page >= totalPages - 1 || loading}
            size="small"
          >
            <NavigateNext />
          </IconButton>
        </Tooltip>

        <Tooltip title="Last Page">
          <IconButton
            onClick={handleLastPage}
            disabled={page >= totalPages - 1 || loading}
            size="small"
            sx={{
              backgroundColor: properties?.colors?.bgcolorbluethem || "#1976d2",
              color: "white",
              "&:hover": {
                backgroundColor: properties?.colors?.bgcolorbluethem || "#1565c0",
              },
              "&.Mui-disabled": {
                backgroundColor: "#e0e0e0",
                color: "#9e9e9e",
              },
            }}
          >
            <LastPage />
          </IconButton>
        </Tooltip>
      </Box>
    </Box>
  );
}

const ReceiveAsset = () => {
  const [employees, setEmployees] = useState([]);
  const [assets, setAssets] = useState([]);
  const [selectedEmployeeId, setSelectedEmployeeId] = useState("");
  const [selectedEmployeeName, setSelectedEmployeeName] = useState("");
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(20);
  const [totalRows, setTotalRows] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [openReceiveDialog, setOpenReceiveDialog] = useState(false);
  const [selectedAsset, setSelectedAsset] = useState(null);
  const [returnReason, setReturnReason] = useState("");
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });
  const navigate = useNavigate();

  useEffect(() => {
    const fetchEmployees = async () => {
      try {
        const response = await GET("admin/employees?status=1&page=0&size=1000");
        let data = response?.data?.data?.paginatedData || response?.data?.data || [];

        const normalizedEmployees = data.map(emp => ({
          id: emp.empId,
          empId: emp.empId,
          employeeName: emp.employeeName || emp.empName || `${emp.firstName || ''} ${emp.lastName || ''}`.trim(),
          status: emp.status
        })).filter(emp => emp.status === 1 || emp.status === null);

        setEmployees(normalizedEmployees);
      } catch (error) {
        console.error("Error fetching employees:", error);
        setEmployees([]);
      }
    };

    fetchEmployees();
  }, []);

  const fetchEmployeeAssets = async (empId) => {
    if (!empId) return;

    try {
      setLoading(true);
      const response = await GET(`it-dept/workstation/view/${empId}`);

      if (response?.data?.statusCode === 200) {
        const data = response.data.data || [];
        const formattedData = data.map((item, index) => ({
          ...item,
          id: item.workstationId || index,
        }));

        setAssets(formattedData);
        setTotalRows(formattedData.length);
        setTotalPages(Math.ceil(formattedData.length / pageSize));
      } else {
        setAssets([]);
        setTotalRows(0);
        setTotalPages(0);
      }
    } catch (error) {
      console.error("Error fetching employee assets:", error);
      setAssets([]);
      setTotalRows(0);
      setTotalPages(0);
    } finally {
      setLoading(false);
    }
  };

  const handleEmployeeSelect = (event) => {
    const empId = event.target.value;
    setSelectedEmployeeId(empId);

    if (empId) {
      const employee = employees.find(emp => emp.empId === empId);
      setSelectedEmployeeName(employee?.employeeName || "");
      fetchEmployeeAssets(empId);
    } else {
      setSelectedEmployeeName("");
      setAssets([]);
      setTotalRows(0);
      setTotalPages(0);
    }
  };

  const handleSearch = () => {
    if (selectedEmployeeId) {
      fetchEmployeeAssets(selectedEmployeeId);
    } else {
      setSnackbar({
        open: true,
        message: "Please select an employee first",
        severity: "warning"
      });
    }
  };

  const handleReceiveAsset = (asset) => {
    setSelectedAsset(asset);
    setReturnReason("");
    setOpenReceiveDialog(true);
  };

  const handleConfirmReceive = async () => {
    if (!returnReason.trim()) {
      setSnackbar({
        open: true,
        message: "Please enter a return reason",
        severity: "warning"
      });
      return;
    }

    if (!selectedAsset) {
      setSnackbar({
        open: true,
        message: "No asset selected",
        severity: "error"
      });
      return;
    }

    try {
      setLoading(true);
      const apiUrl = `it-dept/workstation/return_asset/${selectedAsset.empId}/${selectedAsset.workstationId}`;
      const loggedInUserName = window.sessionStorage.getItem("EmpName") || "IT Admin";
      const payload = {
        returnTo: loggedInUserName,
        returnedDate: new Date().toISOString().split('T')[0],
        returnReason: returnReason.trim()
      };
      const response = await PUT(apiUrl, payload);
      if (response.status === 200 || response?.data?.statusCode === 200) {
        setSnackbar({
          open: true,
          message: response?.data?.message || "Asset returned successfully",
          severity: "success"
        });

        setOpenReceiveDialog(false);
        setSelectedAsset(null);
        setReturnReason("");

        if (selectedEmployeeId) {
          fetchEmployeeAssets(selectedEmployeeId);
        }
      } else {
        setSnackbar({
          open: true,
          message: response?.data?.message || "Failed to return asset",
          severity: "error"
        });
      }
    } catch (error) {
      console.error("Error returning asset:", error);
      let errorMessage = "Error returning asset. Please try again.";
      if (error.response) {
        if (error.response.status === 401) {
          errorMessage = "Unauthorized access. Please check your authentication.";
        } else if (error.response.status === 403) {
          errorMessage = "Access forbidden. You don't have permission to perform this action.";
        } else if (error.response.status === 400) {
          errorMessage = error.response.data?.message || "Bad request. Please check your input.";
        } else if (error.response.status === 500) {
          errorMessage = "Server error. Please try again later.";
        } else {
          errorMessage = error.response.data?.message || `Server error (${error.response.status}). Please try again.`;
        }
      } else if (error.request) {
        errorMessage = "Network error. Please check your connection.";
      }

      setSnackbar({
        open: true,
        message: errorMessage,
        severity: "error"
      });
    } finally {
      setLoading(false);
    }
  };

  const handlePageChange = (newPage) => {
    setPage(newPage);
    setTimeout(() => {
      window.scrollTo(0, 0);
    }, 0);
  };

  const handleGoToLastPage = () => {
    const lastPage = totalPages - 1;
    setPage(lastPage);
    setTimeout(() => {
      window.scrollTo(0, 0);
    }, 0);
  };

  const columns = [
    // { field: "id", headerName: "#", width: 20, headerAlign: "left", align: "left" },
    { field: "empId", headerName: "Employee ID", width: 100, headerAlign: "center", align: "center" },
    {
      field: "empName",
      headerName: "Employee Name",
      width: 130,
      align: "left",
      headerAlign: "left"
    },
    {
      field: "assetNo",
      headerName: "Asset No",
      width: 110,
      align: "left",
      headerAlign: "left"
    },
    {
      field: "assetName",
      headerName: "Asset Main Description",
      width: 150,
      align: "left",
      headerAlign: "left"
    },
    {
      field: "assignedBy",
      headerName: "Assigned By",
      width: 110,
      align: "left",
      headerAlign: "left"
    },
    {
      field: "returnTo",
      headerName: "Return To",
      width: 110,
      align: "left",
      headerAlign: "left"
    },
    {
      field: "assignedDate",
      headerName: "Assigned Date",
      width: 120,
      align: "left",
      headerAlign: "left",
      renderCell: (params) =>
        params.value ? new Date(params.value).toLocaleDateString("en-GB") : "-",
    },
    {
      field: "returnedDate",
      headerName: "Returned Date",
      width: 120,
      align: "left",
      headerAlign: "left",
      renderCell: (params) =>
        params.value ? new Date(params.value).toLocaleDateString("en-GB") : "-",
    },
    {
      field: "actions",
      headerName: "Actions",
      width: 140,
      headerAlign: "left",
      align: "left",
      cellClassName: "actions",
      renderCell: (params) => {
        const canReceive = !params.row.returnReason || params.row.returnReason === "N/A";

        return (
          <div>
            {canReceive ? (
              <MDButton
                variant="contained"
                size="small"
                color="success"
                onClick={() => handleReceiveAsset(params.row)}
                sx={{
                  fontFamily: properties.fontFamily,
                  background: "#4caf50",
                  "&:hover": {
                    background: "#45a049"
                  }
                }}
              >
                Receive Asset
              </MDButton>
            ) : (
              <Box
                sx={{
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  backgroundColor: "#e8f5e9",
                  color: "#2e7d32",
                  borderRadius: "20px",
                  fontFamily: properties.fontFamily,
                  fontWeight: 600,
                  fontSize: "13px",
                  px: 1.8,
                  py: 0.6,
                  border: "1px solid #c8e6c9",
                  lineHeight: 1,
                  textAlign: "center",
                  cursor: "default",
                  userSelect: "none",
                  minWidth: "85px",
                }}
              >
                Returned
              </Box>
            )}
          </div>
        );
      },
    },
  ];

  function CustomNoRowsOverlay() {
    return (
      <Typography
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          height: "100%",
          fontFamily: properties.fontFamily,
          fontSize: "16px",
          color: "textPrimary",
          fontWeight: "bold"
        }}
      >
        Please select an employee to view assigned assets.
      </Typography>
    );
  }

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox>
        <AppBar position="static">
          <Card>
            <MDBox container pt={2} pb={2} px={3}>
              <MDBox
                sx={{
                  display: "grid",
                  gridTemplateColumns: "repeat(12, 1fr)",
                  gap: 1,
                  alignItems: "center"
                }}
              >
                <Typography
                  sx={{
                    gridColumn: "span 2",
                    color: "textPrimary",
                    fontFamily: properties.fontFamily,
                    fontSize: "15px",
                    fontWeight: "bold"
                  }}
                >
                  Receive Asset
                </Typography>
                <MDInput
                  sx={{ gridColumn: "span 2" }}
                  value={selectedEmployeeId}
                  onChange={(e) => setSelectedEmployeeId(e.target.value)}
                  onKeyDown={(event) => {
                    if (event.key === "Enter") {
                      handleSearch();
                    }
                  }}
                  name="employeeId"
                  label="Employee ID"
                  variant="outlined"
                />
                <FormControl variant="outlined" sx={{ m: 0, width: "20ch", gridColumn: "span 3" }}>
                  <InputLabel id="employee-select">Select Employee</InputLabel>
                  <Select
                    labelId="employee-select"
                    id="employee-select"
                    name="selectedEmployeeId"
                    value={selectedEmployeeId}
                    onChange={handleEmployeeSelect}
                    onKeyDown={(event) => {
                      if (event.key === "Enter") {
                        handleSearch();
                      }
                    }}
                    label="Select Employee"
                    sx={{ minWidth: 150, p: 1.5 }}
                  >
                    <MenuItem value=""><em>Select Employee</em></MenuItem>
                    {employees.map((emp) => (
                      <MenuItem key={emp.empId} value={emp.empId} sx={{ fontFamily: properties.fontFamily }}>
                        {emp.employeeName} [{emp.empId}]
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <MDButton
                  sx={{
                    gridColumn: "span 2",
                    fontFamily: properties.fontFamily,
                    background: properties.colors.bgcolorbluethem
                  }}
                  variant="contained"
                  color="info"
                  onClick={handleSearch}
                  disabled={loading}
                >
                  {loading ? "Searching..." : "Search"}
                </MDButton>
                <MDButton
                  variant="contained"
                  color="secondary"
                  onClick={() => navigate("/assetList")}
                  sx={{ gridColumn: "span 3", fontFamily: properties.fontFamily }}
                >
                  Back to Asset List
                </MDButton>
              </MDBox>
            </MDBox>
          </Card>
        </AppBar>

        <MDBox sx={{ overflow: "auto" }}>
          <Paper>
            <DataGrid
              rows={assets}
              columns={columns}
              paginationMode="server"
              rowCount={totalRows}
              pageSizeOptions={[20]}
              paginationModel={{ page, pageSize }}
              loading={loading}
              getRowId={(row) => row.workstationId || row.id}
              hideFooter
              sortingMode="server"
              sortModel={[]}
              sx={{
                fontFamily: properties.fontFamily,
                fontSize: "14px",
                fontWeight: 400,
                height: "450px",
                "& .MuiDataGrid-scrollbar": {
                  width: "10px !important"
                }
              }}
              slots={{
                noRowsOverlay: CustomNoRowsOverlay,
              }}
            />
            <CustomPagination
              page={page}
              pageSize={pageSize}
              totalRows={totalRows}
              totalPages={totalPages}
              onPageChange={handlePageChange}
              onGoToLastPage={handleGoToLastPage}
              loading={loading}
            />
          </Paper>
        </MDBox>

        <Dialog open={openReceiveDialog} onClose={() => setOpenReceiveDialog(false)} maxWidth="sm" fullWidth>
          <DialogTitle sx={{ fontFamily: properties.fontFamily, fontWeight: "bold" }}>
            Receive Asset
          </DialogTitle>
          <DialogContent>
            {selectedAsset && (
              <MDBox sx={{ mt: 2 }}>
                <MDTypography variant="h6" sx={{ fontFamily: properties.fontFamily, mb: 2 }}>
                  Asset Details
                </MDTypography>
                <MDBox sx={{ mb: 3 }}>
                  <Typography variant="body2" sx={{ fontFamily: properties.fontFamily, fontSize: "14px" }}>
                    <strong>Asset No:</strong> {selectedAsset.assetNo}
                  </Typography>
                  <Typography variant="body2" sx={{ fontFamily: properties.fontFamily, fontSize: "14px" }}>
                    <strong>Asset Main Description:</strong> {selectedAsset.assetName}
                  </Typography>
                  <Typography variant="body2" sx={{ fontFamily: properties.fontFamily, fontSize: "14px" }}>
                    <strong>Employee:</strong> {selectedAsset.empName} [{selectedAsset.empId}]
                  </Typography>
                </MDBox>

                <TextField
                  fullWidth
                  multiline
                  rows={3}
                  label="Return Comment"
                  value={returnReason}
                  onChange={(e) => setReturnReason(e.target.value)}
                  sx={{ fontFamily: properties.fontFamily }}
                />
              </MDBox>
            )}
          </DialogContent>
          <DialogActions>
            <MDButton onClick={() => setOpenReceiveDialog(false)} color="secondary" sx={{ fontFamily: properties.fontFamily }}>
              Cancel
            </MDButton>
            <MDButton
              onClick={handleConfirmReceive}
              color="info"
              disabled={loading || !returnReason.trim()}
              sx={{ fontFamily: properties.fontFamily }}
            >
              {loading ? "Processing..." : "Confirm Receive"}
            </MDButton>
          </DialogActions>
        </Dialog>

        <Snackbar
          open={snackbar.open}
          autoHideDuration={6000}
          onClose={() => setSnackbar({ ...snackbar, open: false })}
        >
          <Alert
            onClose={() => setSnackbar({ ...snackbar, open: false })}
            severity={snackbar.severity}
            sx={{ width: "100%" }}
          >
            {snackbar.message}
          </Alert>
        </Snackbar>
      </MDBox>
    </DashboardLayout>
  );
};

export default ReceiveAsset;
